import { GateLinkedDeviceDirection } from '@/__generated__/graphql';
import { useModalStatusMessage } from '@/hooks';
import { useMutation } from '@apollo/client';
import ConfirmationModal from '@components/modal/ConfirmationModal';
import { AuthorizationWrapper, Access } from '@features/authorization';
import { REMOVE_GATE_LINKED_DEVICE } from '@features/deviceConfigurationModal/graphql/mutations/removeGateLinkedDevice';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { Button, Tooltip } from '@mui/material';
import { useState, type FC } from 'react';

interface Props {
  gateId: string;
  direction: GateLinkedDeviceDirection;
  /** El backend no permite dejar la compuerta sin niveles (AMARU-892). */
  isLastLevel: boolean;
}

export const RemoveLinkedDeviceButton: FC<Props> = ({ gateId, direction, isLastLevel }) => {
  const { _ } = useLingui();
  const { openModalSuccessMessage, openModalErrorMessage } = useModalStatusMessage();
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [mutate] = useMutation(REMOVE_GATE_LINKED_DEVICE, {
    onCompleted() {
      openModalSuccessMessage(_(msg`Se ha quitado el sensor de nivel correctamente`));
      setOpenConfirmation(false);
    },
    onError(error) {
      openModalErrorMessage(_(msg`Error al quitar el sensor de nivel: ${error.message}`));
    },
  });

  const modalTitle =
    direction === GateLinkedDeviceDirection.Upstream
      ? _(msg`Quitar sensor de nivel aguas arriba`)
      : _(msg`Quitar sensor de nivel aguas abajo`);

  const onRemoveClick = () => {
    mutate({ variables: { input: { gateId, direction } } });
  };

  return (
    <AuthorizationWrapper access={Access.RemoveGateLinkedDevices} deviceId={gateId} fallback={null}>
      <>
        <ConfirmationModal
          title={modalTitle}
          open={openConfirmation}
          onClose={() => setOpenConfirmation(false)}
          submitButton={{
            onClick: onRemoveClick,
          }}
        />
        {isLastLevel ? (
          <Tooltip title={<Trans>La compuerta debe tener al menos un sensor de nivel</Trans>}>
            <span>
              <Button size="small" color="error" disabled>
                <Trans>Quitar nivel</Trans>
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button size="small" color="error" onClick={() => setOpenConfirmation(true)}>
            <Trans>Quitar nivel</Trans>
          </Button>
        )}
      </>
    </AuthorizationWrapper>
  );
};
